/* eslint-disable no-param-reassign */
import { useCallback, useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { Icon } from "@mui/material";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "components/Tables/DataTable";

import MDButton from "components/MDButton";
import deviceHelpers from "layouts/device/data/device";
import Loading from "components/Loading";

import { actions } from "../../store/reducers/device";
import { actions as numbersActions } from "../../store/reducers/numbers";
import Modal from "./modal";
import DisconnectModal from "./disconnect";

function Device() {
  const dispatch = useDispatch();

  const { getRows, columns } = deviceHelpers();
  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [deviceIsDisconnecting, setDeviceIsDisconnecting] = useState(false);
  const [deviceToDisconnect, setDeviceToDisconnect] = useState(null);

  const { devices, totalItems, page, perPage, qs, loading } = useSelector((state) => state.devices);

  const locations = useSelector((state) => state.numbers.locations);
  const timezones = useSelector((state) => state.numbers.timezones);

  const [open, setOpen] = useState(false);
  const [openDisconnect, setOpenDisconnect] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setDeviceToEdit(null);
    setOpenDisconnect(false);
    setDeviceToDisconnect(null);
  }, [setOpen, setDeviceToEdit, setOpenDisconnect, setDeviceToDisconnect]);

  const submit = useCallback(
    (data, call) => {
      if (deviceToEdit) {
        dispatch(
          actions.updateDevice(
            deviceToEdit.id,
            data,
            () => {
              toast.success("Device updated successfully");
              handleClose();
            },
            (msg, error) => {
              toast.error(msg || "Device could not be updated");
              call(error);
            }
          )
        );
      } else {
        dispatch(
          actions.addDevice(
            data,
            () => {
              toast.success("Device created successfully");
              handleClose();
            },
            (msg, error) => {
              toast.error(msg || "Device could not be created");
              call(error);
            }
          )
        );
      }
    },
    [deviceToEdit, dispatch, handleClose]
  );

  const disconnect = useCallback(() => {
    if (deviceToDisconnect) {
      setDeviceIsDisconnecting(true);
      dispatch(
        actions.updateDevice(
          deviceToDisconnect.id,
          {
            ...deviceToDisconnect,
            connected: false,
            deviceMacAddress: "",
          },
          () => {
            toast.success("Device updated successfully");
            handleClose();
          },
          (msg) => {
            toast.error(msg || "Device could not be updated");
          }
        )
      );
    }
  }, [deviceToDisconnect, dispatch, handleClose]);

  useEffect(() => {
    dispatch(numbersActions.fetchLocationRequest((err) => toast.error(err)));
    dispatch(numbersActions.fetchTimezoneRequest((err) => toast.error(err)));
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.fetchDeviceRequest(page, perPage, (err) => toast.error(err)));
  }, [page, perPage, qs]);

  const handleExport = () =>
    dispatch(actions.exportDeviceRequest(page, perPage, (err) => toast.error(err)));
  const rows = useMemo(
    () =>
      getRows(
        devices,
        locations.reduce((all, l) => {
          all[l.id] = l;
          return all;
        }, {}),
        timezones.reduce((all, l) => {
          all[l.id] = l;
          return all;
        }, {}),
        setDeviceToEdit,
        setDeviceToDisconnect
      ),
    [devices, locations, timezones, setDeviceToEdit, setDeviceToDisconnect]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loading />}
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Physical Clocks
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <MDBox pl={3}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="medium"
                    disabled={totalItems === 0}
                    aria-label="close"
                    onClick={handleExport}
                  >
                    <Icon fontSize="medium">download</Icon> Export CSV
                  </MDButton>
                  <MDButton
                    style={{ marginLeft: "20px" }}
                    variant="gradient"
                    color="primary"
                    size="medium"
                    aria-label="close"
                    onClick={handleOpen}
                  >
                    <Icon fontSize="medium">add</Icon> Add Device
                  </MDButton>
                </MDBox>

                <DataTable
                  entriesPerPage={{ defaultValue: perPage, entries: [5, 10, 15, 20, 50] }}
                  table={{ columns, rows }}
                  isSorted={false}
                  showTotalEntries
                  noEndBorder
                  canSearch
                  totalCount={totalItems}
                  page={page}
                  perPage={perPage}
                  qs={qs}
                  setQs={(s) => dispatch(actions.setDeviceQs(s))}
                  gotoPage={(p) => dispatch(actions.setDevicePage(p))}
                  setPageSize={(p) => dispatch(actions.setDevicePerPage(p))}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Modal
        open={!!(open || deviceToEdit)}
        handleClose={handleClose}
        onSumbit={submit}
        data={deviceToEdit}
        isEdit={deviceToEdit !== null}
        locations={locations}
        timezones={timezones}
      />
      <DisconnectModal
        open={!!(openDisconnect || deviceToDisconnect)}
        handleClose={handleClose}
        onSumbit={disconnect}
        data={deviceToDisconnect}
        isSubmitting={deviceIsDisconnecting}
      />
    </DashboardLayout>
  );
}

export default Device;
